import React from "react";
import { DateTime } from "luxon";
import { TListingValuationTabOutput } from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { CRMCardOutsideSharpCorners } from "../CRMCardOutsideSharpCorners/CRMCardOutsideSharpCorners";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { MISSING_LABELS } from "./valuationTabSteps";

const formatDate = (isoDate: string): string =>
    DateTime.fromISO(isoDate).setZone("Europe/London").toFormat("d MMM");

const whereItsUpTo = (output: TListingValuationTabOutput): string => {
    if (output.valuation_sent) {
        return "Sent to the vendor";
    }
    if (output.market_price_pence !== null) {
        return "Price ready — not sent to the vendor yet";
    }
    if (output.steps.some((step) => step.status === "pending")) {
        return "Still gathering data — no price yet";
    }
    if (output.steps.length === 0) {
        return "Not started yet";
    }
    return "No price yet";
};

const missingData = (output: TListingValuationTabOutput): string | null => {
    const failed = output.steps.filter((step) => step.status === "failed");
    const pending = output.steps.filter((step) => step.status === "pending");

    if (output.steps.length === 0) {
        return "Nothing has been collected for this property yet. Use Collect the data"
            + " below to start it.";
    }
    if (failed.length > 0) {
        return `We couldn't get ${failed.map((step) => MISSING_LABELS[step.step]).join(" and ")}.`
            + " We keep retrying, so this may fill in on its own.";
    }
    if (pending.length > 0) {
        return `Still waiting for ${pending.map((step) => MISSING_LABELS[step.step]).join(" and ")}.`;
    }
    return null;
};

export const CRMValuationStatusBar = (props: { output: TListingValuationTabOutput }): JSX.Element => {
    const { output } = props;
    const missing = missingData(output);

    return (
        <CRMCardOutsideSharpCorners>
            <Background padding={`${CRMSpacing.MEDIUM} ${CRMSpacing.LARGE}`} color={CRMColors.NEUTRAL_12}>
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    <SpacingRow spacing={CRMSpacing.MEDIUM} childSize="auto 1fr" alignItems="center">
                        <Text size={CRMFontSizes.MED}><WeightBold>{whereItsUpTo(output)}</WeightBold></Text>
                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                            {[
                                output.valuation_date === null
                                    ? null
                                    : `Booked ${formatDate(output.valuation_date)}`,
                                output.valuation_sent_deferred === null
                                    ? null
                                    : `sending snoozed to ${formatDate(output.valuation_sent_deferred)} — it won't send on its own`,
                            ].filter((part) => part !== null).join(" · ")}
                        </Text>
                    </SpacingRow>

                    {missing !== null &&
                        <Text
                            size={CRMFontSizes.SMALL}
                            color={output.steps.some((step) => step.status === "failed")
                                ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0
                                : CRMColors.NEUTRAL_2
                            }
                        >
                            {missing}
                        </Text>
                    }
                </SpacingColumn>
            </Background>
        </CRMCardOutsideSharpCorners>
    );
};
